import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Calendar, Pocket } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetPettyCashReconciliationQuery,
  useApprovePettyCashReconciliationMutation,
  useRejectPettyCashReconciliationMutation,
} from "../../pettyCashApi";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import { PETTY_CASH_RECONCILIATION_STATUS_CLASS, PETTY_CASH_RECONCILIATION_STATUS_LABEL } from "../../pettyCashStatusMaps";
import { useCanApproveReconciliation, useCanSelfApprove } from "../../usePettyCashPermissions";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";

const money = (n) => (n == null ? "-" : Number(n).toFixed(2));

const ReconciliationDetail = () => {
  const { recordGuid } = useParams();
  const { userId } = useClaims();
  const canApprove = useCanApproveReconciliation();
  const canSelfApprove = useCanSelfApprove();

  const { data: reconciliation, isLoading, isError, error } = useGetPettyCashReconciliationQuery(recordGuid);
  const [approveReconciliation] = useApprovePettyCashReconciliationMutation();
  const [rejectReconciliation] = useRejectPettyCashReconciliationMutation();

  const [showRejectForm, setShowRejectForm] = useState(false);

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }
  if (isError || !reconciliation) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.pettyCashReconciliations} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Reconciliations
          </Link>
          <div className="alert alert-danger">Could not load this reconciliation ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  // By user id - names are display text and two people can share one.
  const isOwner = userId != null && reconciliation.submittedBy === userId;
  const isSubmitted = reconciliation.status === "SUBMITTED";
  const canApproveOrRejectNow = isSubmitted && (isOwner ? canSelfApprove : canApprove);

  const handleApprove = async () => {
    try {
      await approveReconciliation({ recordGuid }).unwrap();
      toast.success("Reconciliation approved");
    } catch (e) {
      toast.error(e?.message || "Could not approve the reconciliation");
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await rejectReconciliation({ recordGuid, rejectionReason }).unwrap();
      toast.success("Reconciliation rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the reconciliation");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashReconciliations} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Reconciliations
              </Link>
              <h4>
                {reconciliation.fundName}{" "}
                <StatusBadge status={reconciliation.status} classMap={PETTY_CASH_RECONCILIATION_STATUS_CLASS} labelMap={PETTY_CASH_RECONCILIATION_STATUS_LABEL} />
              </h4>
              <h6>{reconciliation.branchCode}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap">
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove}>
                <CheckCircle size={16} /> Approve
              </button>
            )}
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                <XCircle size={16} /> Reject
              </button>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body d-flex align-items-center gap-3">
            <span className="master-detail-avatar master-detail-avatar-lg">
              {(reconciliation.submittedByName || "?").trim().slice(0, 2).toUpperCase()}
            </span>
            <div className="flex-grow-1">
              <h5 className="mb-0">{reconciliation.submittedByName}</h5>
              <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                <span className="d-inline-flex align-items-center"><Calendar size={14} className="me-1" />{new Date(reconciliation.reconciliationDate).toLocaleDateString()}</span>
                <span className="d-inline-flex align-items-center"><Pocket size={14} className="me-1" />Difference: {money(reconciliation.difference)}</span>
              </div>
            </div>
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}

        {reconciliation.status === "REJECTED" && reconciliation.rejectionReason && (
          <div className="alert alert-danger">Rejected by {reconciliation.rejectedByName}: {reconciliation.rejectionReason}</div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row mb-3">
              <div className="col-md-4"><strong>System Balance:</strong> {money(reconciliation.systemBalance)}</div>
              <div className="col-md-4"><strong>Physical Balance:</strong> {money(reconciliation.physicalBalance)}</div>
              <div className="col-md-4"><strong>Difference:</strong> {money(reconciliation.difference)}</div>
              {reconciliation.explanation && <div className="col-md-12 mt-2"><strong>Explanation:</strong> {reconciliation.explanation}</div>}
            </div>
            <div className="text-muted small">
              Submitted by <ActorName action="Submitted" name={reconciliation.submittedByName} /> on {new Date(reconciliation.submittedDate).toLocaleString()}
              {reconciliation.approvedByName && (
                <> - Approved by <ActorName action="Approved" name={reconciliation.approvedByName} /> on {new Date(reconciliation.approvedDate).toLocaleString()}</>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationDetail;
